import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight, ArrowUpRight } from "lucide-react";
import { HOME } from "@/constants/testIds";
import { photoUrl, isVideo } from "@/lib/api";
import { SectionHeader } from "./Services";
import { playMouseClick } from "@/lib/sounds";

export default function Gallery({ photos, content }) {
  const [idx, setIdx] = useState(null);
  const g = content?.gallery || {};
  // Skip the photos already used in hero/about/broadcasting to keep things varied (optional)
  return (
    <section id="galeria" data-testid={HOME.gallery} className="relative py-24 sm:py-28 px-5 sm:px-8 lg:px-12 bg-surface border-y border-line-2">
      <div className="max-w-7xl mx-auto">
        <SectionHeader eyebrow={g.kicker || "Trabajos"} title={g.title || "LUVA en acción"} sub={g.body} />
        <div className="flex flex-wrap gap-2 mt-6">
          {(g.tags || []).map((t, i) => (
            <span key={i} className="px-3 py-1 border border-line text-fg-2 text-[12px]">{t}</span>
          ))}
        </div>
        <div className="mt-12 grid grid-cols-2 md:grid-cols-3 gap-3">
          {photos.map((p, i) => (
            <motion.button key={p.id || p.filename}
              initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.45, delay: (i%6)*0.05 }}
              onClick={() => { playMouseClick(); setIdx(i); }}
              className={`img-hover relative group bg-base ${i % 5 === 0 ? "md:row-span-2 aspect-[3/4] md:aspect-auto" : "aspect-[4/3]"}`}
            >
              {isVideo(p)
                ? <video src={photoUrl(p)} autoPlay muted loop playsInline className="absolute inset-0 w-full h-full object-cover" />
                : <img src={photoUrl(p)} alt={p.title} loading="lazy" className="absolute inset-0 w-full h-full object-cover" />}
              <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/0 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
              <div className="absolute bottom-0 left-0 right-0 p-4 opacity-0 group-hover:opacity-100 transition-opacity">
                <div className="flex items-center justify-between">
                  <div className="text-fg text-[14px] font-semibold">{p.title}</div>
                  <ArrowUpRight className="w-4 h-4 text-fg" />
                </div>
              </div>
            </motion.button>
          ))}
        </div>
      </div>
      <AnimatePresence>
        {idx !== null && (
          <motion.div className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4" initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }} onClick={() => setIdx(null)}>
            <button onClick={(e)=>{e.stopPropagation(); setIdx((i)=>(i-1+photos.length)%photos.length);}} className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 border border-line text-fg flex items-center justify-center bg-base"><ChevronLeft className="w-5 h-5" /></button>
            <button onClick={(e)=>{e.stopPropagation(); setIdx((i)=>(i+1)%photos.length);}} className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 border border-line text-fg flex items-center justify-center bg-base"><ChevronRight className="w-5 h-5" /></button>
            <button onClick={() => setIdx(null)} className="absolute top-4 right-4 w-11 h-11 border border-line text-fg bg-base flex items-center justify-center"><X className="w-5 h-5" /></button>
            {isVideo(photos[idx])
              ? <motion.video key={photos[idx].filename} src={photoUrl(photos[idx])} autoPlay muted loop playsInline controls initial={{ opacity:0 }} animate={{ opacity:1 }} className="max-w-[92vw] max-h-[82vh] object-contain" onClick={(e)=>e.stopPropagation()} />
              : <motion.img key={photos[idx].filename} src={photoUrl(photos[idx])} initial={{ opacity:0 }} animate={{ opacity:1 }} className="max-w-[92vw] max-h-[82vh] object-contain" onClick={(e)=>e.stopPropagation()} />}
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 bg-base border border-line px-4 py-2 text-fg-2 text-[13px]">
              {photos[idx].title || ""} · {idx+1}/{photos.length}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
